/**
 * "Pontos para a consulta": the plan measured without AI. Calories against the questionnaire's goal (when there is
 * one), protein per kg, fiber, how the calories spread over the meals, and variety. The API's dietPlanPoints only
 * words points from these numbers; it never sees the plan's photo.
 *
 * The only one-tap changes offered come from the plan itself: its options ("usar a opção 2") and its substitution
 * list, never a food the nutritionist didn't write.
 */
import { calculateAll } from './nutrition';
import { dayTotalsOf } from './freeMeal';
import { macroShares } from './dietPlanView';
import { chooseOption, foodsTotals, substitutesFor, syncActiveOption } from './dietImport';

const round = (value) => Math.round(value || 0);
const oneDecimal = (value) => Math.round((value || 0) * 10) / 10;

/** The questionnaire's weight in kg, or null. */
export const questionnaireWeightKg = (questionnaire) => {
  const weight = parseFloat(questionnaire?.weight);
  if (!Number.isFinite(weight) || weight <= 0) return null;
  return questionnaire.useImperial ? weight * 0.453592 : weight;
};

/** The calories the questionnaire's answers aim at, or null when they're missing. */
export const questionnaireGoal = (questionnaire) => {
  const q = questionnaire || {};
  const weightKg = questionnaireWeightKg(q);
  const heightCm = q.useImperial ? ((parseFloat(q.heightFt) || 0) * 12 + (parseFloat(q.heightIn) || 0)) * 2.54 : parseFloat(q.height);
  const age = parseInt(q.age, 10);
  if (!weightKg || !(heightCm > 0) || !(age > 0) || !q.gender || !q.activityLevel || !q.goal) return null;
  return { calories: calculateAll(weightKg, heightCm, age, q.gender, q.activityLevel, q.goal).targetCalories, kind: q.goal };
};

const dayFiber = (day) => (day.meals || []).flatMap((meal) => meal.foods || []).reduce((sum, food) => sum + (food.fiber || 0), 0);
const hasFiberData = (weekPlan) => weekPlan.some((day) => (day.meals || []).some((meal) => (meal.foods || []).some((food) => food.fiber != null)));
const foodNamesOf = (meal) => [...(meal.foods || []), ...(meal.options || []).flatMap((option) => option.foods || [])].map((food) => String(food.name).toLowerCase().trim());
const dayKey = (day) => JSON.stringify((day.meals || []).map((meal) => (meal.foods || []).map((food) => food.name)));

/**
 * The changes the plan itself allows: a meal's other option every day, its options alternated over the week, or a food
 * swapped for one of its listed substitutes on alternate days. Each with what it changes (calories, protein).
 */
export const planActions = (weekPlan, substitutions) => {
  const days = (weekPlan || []).filter((day) => day.meals?.length);
  if (!days.length) return [];
  const first = days[0];
  const actions = [];
  const average = (values) => values.reduce((sum, value) => sum + value, 0) / (values.length || 1);

  first.meals.forEach((meal, mealIndex) => {
    const count = meal.options?.length || 0;
    if (count < 2) return;
    const chosen = days.map((day) => day.meals[mealIndex]?.optionIndex ?? 0);
    const now = days.map((day) => foodsTotals(day.meals[mealIndex]?.foods));
    const nowKcal = average(now.map((totals) => totals.calories));
    const nowProtein = average(now.map((totals) => totals.protein));
    meal.options.forEach((option, optionIndex) => {
      if (chosen.every((index) => index === optionIndex)) return;
      const totals = foodsTotals(option.foods);
      actions.push({
        id: `opt:${mealIndex}:${optionIndex}`,
        kind: 'option',
        meal: mealIndex,
        mealName: meal.name,
        option: optionIndex,
        delta: { calories: round(totals.calories - nowKcal), protein: round(totals.protein - nowProtein) },
      });
    });
    const isAlternating = days.every((day, position) => chosen[position] === day.dayIndex % count);
    if (!isAlternating) {
      const alternated = days.map((day) => foodsTotals(meal.options[day.dayIndex % count].foods));
      actions.push({
        id: `alt:${mealIndex}`,
        kind: 'alternate',
        meal: mealIndex,
        mealName: meal.name,
        delta: {
          calories: round(average(alternated.map((totals) => totals.calories)) - nowKcal),
          protein: round(average(alternated.map((totals) => totals.protein)) - nowProtein),
        },
      });
    }
  });

  (substitutions || []).forEach((group, groupIndex) => {
    let found = null;
    first.meals.some((meal, mealIndex) =>
      (meal.foods || []).some((food) => {
        if (substitutesFor([group], food).length === 0) return false;
        found = { mealIndex, meal, food };
        return true;
      })
    );
    if (!found) return;
    substitutesFor([group], found.food)
      .slice(0, 2)
      .forEach((item) => {
        const itemIndex = (group.items || []).indexOf(item);
        actions.push({
          id: `sub:${groupIndex}:${itemIndex}`,
          kind: 'substitution',
          meal: found.mealIndex,
          mealName: found.meal.name,
          foodName: found.food.name,
          item,
          delta: { calories: round((item.calories || 0) - (found.food.calories || 0)), protein: round((item.protein || 0) - (found.food.protein || 0)) },
        });
      });
  });

  return actions.slice(0, 8);
};

/** The week with an action applied. Substitutes go in on the alternate days (Tuesday, Thursday, Saturday). */
export const applyAction = (weekPlan, action) =>
  weekPlan.map((day) => ({
    ...day,
    meals: day.meals.map((meal, mealIndex) => {
      if (mealIndex !== action.meal) return meal;
      if (action.kind === 'option') return chooseOption(meal, action.option);
      if (action.kind === 'alternate' && meal.options?.length > 1) return chooseOption(meal, day.dayIndex % meal.options.length);
      if (action.kind === 'substitution' && day.dayIndex % 2 === 1) {
        const at = (meal.foods || []).findIndex((food) => food.name === action.foodName);
        if (at === -1) return meal;
        return syncActiveOption({ ...meal, foods: meal.foods.map((food, index) => (index === at ? action.item : food)) });
      }
      return meal;
    }),
  }));

/**
 * The numbers the analysis shows and sends: everything is measured here. `weightKg` is the questionnaire's, or the
 * one typed on the analysis (kept on that screen only). `labelOf` words each action for the API and the button.
 */
export const planNumbers = ({ weekPlan, origin, questionnaire, weightKg = null, substitutions, labelOf = (action) => action.id, effectOf = () => '' }) => {
  const days = (weekPlan || []).filter((day) => day.meals?.length);
  if (!days.length) return null;
  const totals = days.map((day) => dayTotalsOf(day.meals));
  const average = (key) => totals.reduce((sum, day) => sum + day[key], 0) / totals.length;
  const calories = average('calories');
  const goal = questionnaireGoal(questionnaire);
  const shares = Object.fromEntries(macroShares({ protein: average('protein'), carbs: average('carbs'), fat: average('fat') }).map((macro) => [macro.key, macro.pct]));
  const kg = weightKg || questionnaireWeightKg(questionnaire);
  const first = days[0];
  const firstKcal = dayTotalsOf(first.meals).calories || 1;
  const actions = planActions(days, substitutions);

  return {
    origin: origin || 'ai',
    sameEveryDay: days.every((day) => dayKey(day) === dayKey(first)),
    calories: {
      plan: round(calories),
      min: round(Math.min(...totals.map((day) => day.calories))),
      max: round(Math.max(...totals.map((day) => day.calories))),
      goal: goal ? goal.calories : null,
      goalKind: goal ? goal.kind : null,
    },
    protein: { grams: round(average('protein')), share: shares.protein, perKg: kg ? oneDecimal(average('protein') / kg) : null },
    carbs: { grams: round(average('carbs')), share: shares.carbs },
    fat: { grams: round(average('fat')), share: shares.fat },
    fiber: { grams: hasFiberData(days) ? round(days.reduce((sum, day) => sum + dayFiber(day), 0) / days.length) : null },
    meals: first.meals.map((meal) => {
      const mealTotals = foodsTotals(meal.foods);
      return {
        name: meal.name,
        time: meal.time || null,
        kcal: round(mealTotals.calories),
        share: round((mealTotals.calories / firstKcal) * 100),
        protein: round(mealTotals.protein),
        options: meal.options?.length || 1,
      };
    }),
    variety: { distinctFoods: new Set(days.flatMap((day) => day.meals.flatMap(foodNamesOf))).size, sameEveryDay: days.every((day) => dayKey(day) === dayKey(first)) },
    estimates: first.meals.flatMap((meal) => [...(meal.options || [{ foods: meal.foods }]).flatMap((option) => option.foods || [])]).filter((food) => food.source === 'estimate').length,
    actions: actions.map((action) => ({ id: action.id, label: labelOf(action), effect: effectOf(action) })),
  };
};

/** The plan as it stands differs from the one the nutritionist wrote (then "Voltar ao plano do nutricionista"). */
export const differsFromOriginal = (weekPlan, originalWeekPlan) =>
  !!originalWeekPlan?.length && JSON.stringify(weekPlan.map((day) => day.meals.map((meal) => meal.foods))) !== JSON.stringify(originalWeekPlan.map((day) => day.meals.map((meal) => meal.foods)));
